import { Injectable } from '@angular/core';
import {
	defer,
	filter,
	finalize,
	first,
	Observable,
	share,
	Subject,
	Subscriber,
	Subscription,
	takeUntil
} from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { v5 as uuidv5 } from 'uuid';
import { INgsiSubscriptionWidget, IPlugin, IUser, ModelService, PluginInjectorService } from 'core-client/api';
import { SocketIoService, ISocketTokenAuth } from './shared/services/socket-io/socket-io.service';
import {
	IQueryIdPatternArgs,
	INotification,
	IPluginSettingsExtended,
	ISubscriptionArgs,
	IEntity,
	IApplicationPluginExtended,
	IDeleteSubscriptionArgs
} from './shared/interfaces/ngsild/ngsi-ld.interface';
import {
	NGSI_LD_API_EVENTS,
	PLUGIN_CONFIG_EVENTS,
	NGSI_LD_NOTIFICATION_EVENTS
} from './shared/constants/event-constants';
import { PERMISSION_REF_NGSI_DATA_CONSUMER } from './shared/constants/permission-constant';

/**
 * NGSILD Connector Service
 *
 * Responsibilities:
 * - Plugin lifecycle management
 * - Notification stream creation and distribution
 * - Widget handler registration with dashboard tracking
 * - Backend communication (queries, subscriptions)
 * - Plugin state coordination with SocketIoService
 *
 * Architecture:
 * - Creates shared notification streams (base + geoQ)
 * - Distributes notifications to registered widget handlers
 * - Delegates socket connection management to SocketIoService
 * - Notifies SocketIoService about plugin enable/disable state
 */
@Injectable({
	providedIn: 'root'
})
export class NgsildConnectorService {
	private plugin: IApplicationPluginExtended = <IApplicationPluginExtended>{};
	private readonly pluginName: string = 'NGSILD_PLUGIN';

	private ngUnsubscribe$: Subject<void> = new Subject<void>();

	// Shared Notification Streams
	private readonly notifications$: Observable<INotification> =
		this.createNotificationStream(NGSI_LD_NOTIFICATION_EVENTS.NOTIFICATION);
	private readonly geoQNotifications$: Observable<INotification> =
		this.createNotificationStream(NGSI_LD_NOTIFICATION_EVENTS.GEOQ_NOTIFICATION);

	// Widget-specific handlers
	private readonly widgetNotificationHandlers: Map<string, (notification: INotification) => void> = new Map();
	private readonly widgetGeoQHandlers: Map<string, (notification: INotification) => void> = new Map();

	// Race condition protection flags
	private isNotificationStreamInitializing: boolean = false;
	private isGeoQNotificationStreamInitializing: boolean = false;

	// Internal subs to shared streams
	private notificationStreamSub?: Subscription;
	private geoQNotificationStreamSub?: Subscription;

	constructor(
		public readonly socket: SocketIoService,
		private readonly modelService: ModelService,
		private readonly pluginInjectorService: PluginInjectorService,
		private readonly snackBar: MatSnackBar,
		private readonly translator: TranslateService,
	) {}

	setup(): void {
		this.initPlugin();
		this.start();
	}

	private initPlugin(): void {
		this.plugin.name = this.pluginName.toUpperCase();
		this.plugin.enabled = false;
		this.plugin.description = 'Plugin service that connects to the ADDIX NGSI-LD API.';
		this.plugin.settings = {
			context_broker_url: '',
			client_id: '',
			client_secret: '',
			realm: ''
		};
		this.plugin.version = '0.0.1';

		this.pluginInjectorService.register(this.plugin);
	}

	/**
	 * Execute socket.emit with callback ack and return result as observable
	 * Uses parent Socket's emit method directly
	 */
	private emitAck<T>(event: string, ...args: any[]): Observable<T> {
		return defer(() => new Observable<T>((observer: Subscriber<T>) => {
			this.socket.emit(event, ...args, (result: T) => {
				observer.next(result);
				observer.complete();
			});
		})).pipe(first());
	}

	/**
	 * Create notification stream with persistent connection
	 *
	 * resetOnRefCountZero is set to FALSE to prevent stream teardown when page visibility changes.
	 * The whenPageVisible$() operator will pause/resume
	 * notifications without destroying the underlying socket subscription.
	 *
	 * Reason:
	 * - IdleService.whenPageVisible$() uses takeUntil(pageHidden$) which unsubscribes when page becomes hidden
	 * - If resetOnRefCountZero were TRUE, the stream would tear down when ref count hits 0
	 * - Stream teardown triggers socket unsubscription → handler unregistration → socket disconnect
	 * - Setting to FALSE keeps the stream alive; notifications just pause/resume via the operator
	 */
	private createNotificationStream(event: string): Observable<INotification> {
		return new Observable<INotification>((observer: Subscriber<INotification>) => {
			console.log(`[NGSILD Connector] New subscriber to event: ${event}`);

			// Forward incoming notifications from socket service
			const sub = this.socket.fromEvent(event)
				.pipe(takeUntil(this.ngUnsubscribe$))
				.subscribe(observer);

			return () => {
				console.log(`[NGSILD Connector] Unsubscribing from socket event: ${event}`);
				sub.unsubscribe(); // unsubscribing stream is handled globally
			};
		}).pipe(
			// Keep stream alive even when no active subscribers
			// This prevents socket handler unregistration during tab switches/page hidden events
			share({ resetOnRefCountZero: false })
		);
	}

	/**
	 * Register notification handler for widget
	 * Now includes dashboardId for proper cleanup when switching dashboards
	 */
	registerNotificationHandler(
		widgetId: string,
		dashboardId: string,
		pluginModule: string,
		handler: (notification: INotification) => void
	): void {
		if (this.widgetNotificationHandlers.has(widgetId)) {
			return;
		}

		console.info(`[NGSILD Connector] Register notification for widgetId: ${widgetId} of pluginModule: ${pluginModule} on dashboard: ${dashboardId}`);
		this.initializeNotificationStream();
		this.widgetNotificationHandlers.set(widgetId, handler);

		// Register handler with socket service (now with dashboardId)
		this.socket.registerHandler(widgetId, dashboardId);
	}

	/**
	 * Register geoNotification handler for widget
	 * Now includes dashboardId for proper cleanup when switching dashboards
	 */
	registerGeoQHandler(
		widgetId: string,
		dashboardId: string,
		pluginModule: string,
		handler: (notification: INotification) => void
	): void {
		if (this.widgetGeoQHandlers.has(widgetId)) {
			return;
		}

		console.info(`[NGSILD Connector] Register geoQ - notification for widgetId: ${widgetId} of pluginModule: ${pluginModule} on dashboard: ${dashboardId}`);
		this.initializeGeoQNotificationStream();
		this.widgetGeoQHandlers.set(widgetId, handler);

		// Register handler with socket service (now with dashboardId)
		this.socket.registerHandler(`${widgetId}_geoq`, dashboardId);
	}

	// Unregister handler for widget
	unregisterNotificationHandler(widgetId: string, pluginModule: string): void {
		console.info(`[NGSILD Connector] Unregister notification for widgetId: ${widgetId} of pluginModule: ${pluginModule}`);
		this.widgetNotificationHandlers.delete(widgetId);
		this.cleanupNotificationSubscriptions();

		// Unregister handler from socket service
		this.socket.unregisterHandler(widgetId);
	}

	// Unregister geo handler for widget
	unregisterGeoQHandler(widgetId: string, pluginModule: string): void {
		console.info(`[NGSILD Connector] Unregister geoQ notification for widgetId: ${widgetId} of pluginModule: ${pluginModule}`);
		this.widgetGeoQHandlers.delete(widgetId);
		this.cleanupNotificationSubscriptions();

		// Unregister handler from socket service
		this.socket.unregisterHandler(`${widgetId}_geoq`);
	}

	/**
	 * Clean up handlers not on active dashboard
	 */
	cleanupInactiveWidgets(activeDashboardId: string): void {
		console.log(`[NGSILD Connector] Cleaning inactive widgets for dashboard: ${activeDashboardId}`);

		// Delegate to socket service for cleanup
		this.socket.cleanupInactiveHandlers(activeDashboardId);

		// Also cleanup local handler maps
		// Note: We don't track dashboardId in connector's maps, so we rely on socket service
		// For now, we just clean up orphaned subscriptions
		this.cleanupNotificationSubscriptions();
	}

	// Subscribe to notifications only once
	private initializeNotificationStream(): void {
		if (this.notificationStreamSub || this.isNotificationStreamInitializing) {
			return;
		}

		this.isNotificationStreamInitializing = true;
		const visible$ = this.pluginInjectorService.idleService.whenPageVisible$()(this.notifications$);

		this.notificationStreamSub = visible$.subscribe((notification: INotification) => {
			this.widgetNotificationHandlers.forEach((handler: (notification: INotification) => void) => handler(notification));
		});

		this.isNotificationStreamInitializing = false;
	}

	private initializeGeoQNotificationStream(): void {
		if (this.geoQNotificationStreamSub || this.isGeoQNotificationStreamInitializing) {
			return;
		}

		this.isGeoQNotificationStreamInitializing = true;
		const visible$ = this.pluginInjectorService.idleService.whenPageVisible$()(this.geoQNotifications$);

		this.geoQNotificationStreamSub = visible$.subscribe((notification: INotification) => {
			this.widgetGeoQHandlers.forEach((handler: (notification: INotification) => void) => handler(notification));
		});

		this.isGeoQNotificationStreamInitializing = false;
	}

	// Configuration changes Events
	transmitConfig$(args: IPlugin): Observable<{ enabled: boolean; error: boolean }> {
		return this.emitAck<{ enabled: boolean; error: boolean }>(PLUGIN_CONFIG_EVENTS.TRANSMIT_CONFIG, args);
	}

	// All query methods
	queryEntityByIdPattern$(args: IQueryIdPatternArgs): Observable<IEntity[]> {
		return this.emitAck<IEntity[]>(NGSI_LD_API_EVENTS.QUERY_ENTITY_BY_ID_PATTERN, args);
	}

	queryEntitiesById$(args: IQueryIdPatternArgs): Observable<IEntity[]> {
		return this.emitAck<IEntity[]>(NGSI_LD_API_EVENTS.QUERY_ENTITIES_BY_ID, args);
	}

	queryEntityById$(args: IQueryIdPatternArgs): Observable<IEntity[]> {
		return this.emitAck<IEntity[]>(NGSI_LD_API_EVENTS.QUERY_ENTITY_BY_ID, args);
	}

	queryEntityByType$(args: IQueryIdPatternArgs): Observable<IEntity[]> {
		return this.emitAck<IEntity[]>(NGSI_LD_API_EVENTS.QUERY_ENTITY_BY_TYPE, args);
	}

	// Subscription - Methods
	createSubscription$(args: ISubscriptionArgs): Observable<number> {
		return this.emitAck<number>(NGSI_LD_API_EVENTS.CREATE_SUBSCRIPTION, args);
	}

	updateSubscription$(curr: ISubscriptionArgs, prev: ISubscriptionArgs): Observable<number> {
		return this.emitAck<number>(NGSI_LD_API_EVENTS.UPDATE_SUBSCRIPTION, curr, prev);
	}

	deleteSubscription$(args: IDeleteSubscriptionArgs): Observable<number> {
		return this.emitAck<number>(NGSI_LD_API_EVENTS.DELETE_SUBSCRIPTION, args);
	}

	// Add or update Subscriptions for receiving notifications on entity
	requestNotificationForEntity(curr: ISubscriptionArgs, prev?: ISubscriptionArgs): void {
		const handleSubscription = (status: number) => {
			switch (status) {
				case 201:
					console.log('[NGSILD Connector] Successfully activated subscription');
					break;
				case 204:
					console.log('[NGSILD Connector] Successfully updated subscription');
					break;
				case 404:
					this.createSubscription$(curr).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(handleSubscription);
					break;
				default:
					console.log('[NGSILD Connector] Subscription handling failed');
			}
		};

		const source$ = prev ? this.updateSubscription$(curr, prev) : this.createSubscription$(curr);
		source$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(handleSubscription);
	}

	private observePluginAction(): void {
		this.modelService._plugin.observePlugin$()
			.pipe(
				filter((data: { plugin: IPlugin, event: 'activate' | 'update' }) =>
					data.plugin.name === this.plugin.name
				),
				takeUntil(this.ngUnsubscribe$),
				finalize(() => console.log('[NGSILD Connector] ObservePlugin$ complete'))
			)
			.subscribe(async (data: { plugin: IPlugin, event: 'activate' | 'update' }) => {
				// Plugin has been activated
				if (data.event === 'activate') {
					this.modelService._plugin.getById(data.plugin._id)
						.then((plugin: IPlugin | {}) => {
							if ('_id' in plugin && 'settings' in plugin && plugin.enabled) {
								const settings = <IPluginSettingsExtended>plugin.settings;
								this.plugin.enabled = data.plugin.enabled;

								// Notify socket service that plugin is enabled
								this.socket.setPluginEnabled(data.plugin.enabled);

								if (this.areSettingsValid(settings)) {
									// Set auth token for socket
									const user = this.modelService._user.getUser();
									this.addAccessRestrictions(user);
								}
							}
						});
				}

				// Plugin has been updated
				if (data.event === 'update') {
					if ('settings' in data.plugin && data.plugin.enabled) {
						this.plugin.enabled = data.plugin.enabled;

						// Notify socket service that plugin is enabled
						this.socket.setPluginEnabled(data.plugin.enabled);

						const user = this.modelService._user.getUser();
						this.addAccessRestrictions(user);
					} else if ('enabled' in data.plugin && !data.plugin.enabled) {
						// Plugin was disabled
						this.plugin.enabled = false;

						// Notify socket service that plugin is disabled
						this.socket.setPluginEnabled(false);
					}

					// Wait for existing socket connection
					const isConnected = await this.waitForOpenConnection(this.socket);
					if (isConnected) {
						this.transmitConfig$(data.plugin)
							.subscribe((result: { enabled: boolean; error: boolean }) => {
								this.handleTransmitResult(result);
							});
					}
				}
			});
	}

	private observeAdditionalPluginAction(): void {
		this.modelService._plugin.observeAdditionalPluginAction$()
			.pipe(
				filter((action: { pluginId: string, pluginModule: string, executeAction: string, data: unknown }) =>
					action.pluginModule === 'MAP'
				),
				takeUntil(this.ngUnsubscribe$),
				finalize(() => console.log('[NGSILD Connector] ObserveAdditionalPluginAction$ completed'))
			)
			.subscribe(async (action: { pluginId: string, pluginModule: string, executeAction: string, data: unknown }) => {
				if (action.executeAction !== 'delete_ngsi_subscription_widget') {
					return;
				}

				const ngsiSubscriptionWidgts = action.data as INgsiSubscriptionWidget[];
				const appPluginWidgetId = ngsiSubscriptionWidgts[0].app_plugin_widget;
				const uuid = await this.uuidFromSettings();

				const subArgs: IDeleteSubscriptionArgs = {
					uuid,
					appPluginWidgetId,
					subscriptions: ngsiSubscriptionWidgts.map((ngsiSubscriptionWidget: INgsiSubscriptionWidget) => ({
						ngsiSubscriptionWidgetId: ngsiSubscriptionWidget._id,
						subscriptionId: ngsiSubscriptionWidget.subscriptionId
					}))
				};

				this.deleteSubscription$(subArgs).pipe(takeUntil(this.ngUnsubscribe$)).subscribe();
			});
	}

	private waitForOpenConnection(socket: SocketIoService): Promise<boolean> {
		return new Promise<boolean>((resolve) => {
			const maxNumberOfAttempts = 10;
			const intervalTime = 200; //ms

			let currentAttempt = 0;
			const interval = setInterval(() => {
				if (currentAttempt > maxNumberOfAttempts - 1) {
					clearInterval(interval);
					resolve(false);
				} else if (socket.ioSocket.connected) {
					clearInterval(interval);
					resolve(true);
				}
				currentAttempt++;
			}, intervalTime);
		});
	}

	private addAccessRestrictions(user: IUser): void {
		const tokenPayload: ISocketTokenAuth = { permissionRef: PERMISSION_REF_NGSI_DATA_CONSUMER, userId: user._id };
		const token = btoa(JSON.stringify(tokenPayload));

		// Delegate auth to socket service
		this.socket.setAuthToken(token);
	}

	private areSettingsValid(settings: IPluginSettingsExtended): boolean {
		return settings.context_broker_url !== '' &&
			settings.client_id !== '' &&
			settings.client_secret !== '' &&
			settings.realm !== '';
	}

	async uuidFromSettings(): Promise<string> {
		const registered = this.pluginInjectorService.getPluginByName(this.plugin.name);
		if (registered) {
			const plugin = await this.modelService._plugin.getById(registered._id);
			if ('_id' in plugin) {
				return this.generateUUID(plugin.settings as IPluginSettingsExtended);
			}
		}

		return this.generateUUID(this.plugin.settings);
	}

	private generateUUID(tenant: { realm: string; client_id: string; client_secret: string; context_broker_url: string; }): string {
		const dataToHash = `${tenant.realm}${tenant.client_id}${tenant.client_secret}${tenant.context_broker_url}`;
		return uuidv5(dataToHash, uuidv5.URL);
	}

	private getMessage(message: string, panelClass: string): void {
		this.snackBar.open(message, '', {
			duration: 2000,
			panelClass: panelClass,
			verticalPosition: 'top',
			horizontalPosition: 'center'
		});
	}

	private handleTransmitResult(result: { enabled: boolean; error: boolean }): void {
		if (result.error) {
			this.getMessage(this.translator.instant('NGSI_NOTIFICATION_PLUGIN.CONNECT_ERROR_MESSAGE'), 'error-snackbar');
		} else if (result.enabled) {
			this.getMessage(this.translator.instant('NGSI_NOTIFICATION_PLUGIN.CONNECT_SUCCESS_MESSAGE'), 'success-snackbar');
			if (!this.socket.ioSocket.connected) {
				this.start();
			}
		} else {
			this.getMessage(this.translator.instant('NGSI_NOTIFICATION_PLUGIN.DISCONNECT_SUCCESS_MESSAGE'), 'success-snackbar');
			if (this.socket.ioSocket.connected) {
				this.stop();
			}
		}
	}

	private cleanupNotificationSubscriptions(): void {
		if (this.widgetNotificationHandlers.size === 0 && this.notificationStreamSub) {
			this.notificationStreamSub.unsubscribe();
			this.notificationStreamSub = undefined;
		}

		if (this.widgetGeoQHandlers.size === 0 && this.geoQNotificationStreamSub) {
			this.geoQNotificationStreamSub.unsubscribe();
			this.geoQNotificationStreamSub = undefined;
		}
	}

	// Start service-wide subscriptions
	start(): void {
		this.resetSubDestroySignal();
		this.observePluginAction();
		this.observeAdditionalPluginAction();
	}

	private resetSubDestroySignal(): void {
		if (this.ngUnsubscribe$.closed) {
			this.ngUnsubscribe$ = new Subject<void>();
		}
	}

	// Stop service-wide subscriptions
	stop(): void {
		this.ngUnsubscribe$.next();
		this.ngUnsubscribe$.complete();

		this.notificationStreamSub?.unsubscribe();
		this.geoQNotificationStreamSub?.unsubscribe();
		this.notificationStreamSub = undefined;
		this.geoQNotificationStreamSub = undefined;

		this.widgetNotificationHandlers.clear();
		this.widgetGeoQHandlers.clear();
	}
}
